import { useState, useRef, useEffect, useCallback } from 'react';

let _micPermissionWarmed = false;

/**
 * Pre-warm mic permission so the browser remembers the grant.
 * Call once at app startup when voice search is enabled.
 */
export function warmMicPermission() {
  if (_micPermissionWarmed) return;
  _micPermissionWarmed = true;
  if (window.__maestroNative) return;
  if (!navigator.mediaDevices?.getUserMedia) return;
  navigator.mediaDevices.getUserMedia({ audio: true })
    .then((stream) => stream.getTracks().forEach((t) => t.stop()))
    .catch(() => {});
}

/**
 * Dedicated microphone hook with proper lifecycle states.
 *
 * State machine:  idle → preparing → listening → idle
 *
 *   idle:      mic is off, nothing happening
 *   preparing: recognition.start() called, waiting for browser/native to acquire mic
 *   listening: audio capturing — mic is genuinely active
 *
 * On iOS (WKWebView), the Web Speech API is unavailable. When
 * window.__maestroNative.hasSpeechRecognition is true, this hook routes
 * through the native SFSpeechRecognizer via the JS bridge instead.
 *
 * Returns: { state, isListening, isPreparing, isSupported,
 *            interimTranscript, start, stop }
 */
export default function useMicrophone({ onResult, onError, language } = {}) {
  const [state, setState] = useState('idle');
  const [isSupported, setIsSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const useNativeRef = useRef(false);
  const lastInterimRef = useRef('');
  const gotFinalRef = useRef(false);

  useEffect(() => {
    onResultRef.current = onResult;
    onErrorRef.current = onError;
  }, [onResult, onError]);

  useEffect(() => {
    const native = window.__maestroNative;

    if (native?.hasSpeechRecognition) {
      useNativeRef.current = true;
      setIsSupported(true);

      const handleStarted = () => {
        gotFinalRef.current = false;
        lastInterimRef.current = '';
        setState('listening');
      };
      const handleEnded = () => {
        if (!gotFinalRef.current && lastInterimRef.current.trim()) {
          const pending = lastInterimRef.current.trim();
          lastInterimRef.current = '';
          setInterimTranscript('');
          onResultRef.current?.(pending, 0.8);
        }
        lastInterimRef.current = '';
        setState('idle');
        setInterimTranscript('');
      };
      const handleResult = (e) => {
        const { transcript, confidence } = e.detail || {};
        gotFinalRef.current = true;
        lastInterimRef.current = '';
        setInterimTranscript('');
        setState('idle');
        onResultRef.current?.(transcript, confidence);
      };
      const handleInterim = (e) => {
        const { transcript } = e.detail || {};
        lastInterimRef.current = transcript || '';
        setInterimTranscript(transcript || '');
      };
      const handleError = (e) => {
        const { error } = e.detail || {};
        if (error === 'aborted') return;
        onErrorRef.current?.(error);
        lastInterimRef.current = '';
        setState('idle');
        setInterimTranscript('');
      };

      window.addEventListener('maestroSpeechStarted', handleStarted);
      window.addEventListener('maestroSpeechEnded', handleEnded);
      window.addEventListener('maestroSpeechResult', handleResult);
      window.addEventListener('maestroSpeechInterim', handleInterim);
      window.addEventListener('maestroSpeechError', handleError);

      return () => {
        window.removeEventListener('maestroSpeechStarted', handleStarted);
        window.removeEventListener('maestroSpeechEnded', handleEnded);
        window.removeEventListener('maestroSpeechResult', handleResult);
        window.removeEventListener('maestroSpeechInterim', handleInterim);
        window.removeEventListener('maestroSpeechError', handleError);
      };
    }

    // Fallback: Web Speech API (works in Safari / desktop browsers, NOT in WKWebView)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    setIsSupported(true);
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = language || navigator.language || 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onaudiostart = () => {
      setState('listening');
    };

    recognition.onresult = (event) => {
      const last = event.results.length - 1;
      const transcript = event.results[last][0].transcript;
      const isFinal = event.results[last].isFinal;
      const confidence = event.results[last][0].confidence;

      if (isFinal) {
        gotFinalRef.current = true;
        lastInterimRef.current = '';
        setInterimTranscript('');
        onResultRef.current?.(transcript, confidence);
        setState('idle');
      } else {
        lastInterimRef.current = transcript;
        setInterimTranscript(transcript);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted') return;
      onErrorRef.current?.(event.error);
      lastInterimRef.current = '';
      setState('idle');
      setInterimTranscript('');
    };

    recognition.onend = () => {
      if (!gotFinalRef.current && lastInterimRef.current.trim()) {
        const pending = lastInterimRef.current.trim();
        lastInterimRef.current = '';
        setInterimTranscript('');
        onResultRef.current?.(pending, 0.8);
      }
      lastInterimRef.current = '';
      setState('idle');
    };

    recognitionRef.current = recognition;
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, [language]);

  const start = useCallback(() => {
    if (state !== 'idle') return;

    if (useNativeRef.current) {
      setState('preparing');
      window.__maestroNative.startSpeechRecognition(language || navigator.language || 'en-US');
      return;
    }

    const rec = recognitionRef.current;
    if (!rec) return;
    try {
      setInterimTranscript('');
      gotFinalRef.current = false;
      lastInterimRef.current = '';
      rec.start();
      setState('preparing');
    } catch (e) {
      if (e.message?.includes('already started')) {
        rec.stop();
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
            setState('preparing');
          } catch {}
        }, 100);
      }
    }
  }, [state, language]);

  const stop = useCallback(() => {
    if (state === 'idle') return;

    if (useNativeRef.current) {
      window.__maestroNative.stopSpeechRecognition();
      setState('idle');
      setInterimTranscript('');
      return;
    }

    const rec = recognitionRef.current;
    if (rec) {
      rec.stop();
      setState('idle');
      setInterimTranscript('');
    }
  }, [state]);

  return {
    state,
    isListening: state === 'listening',
    isPreparing: state === 'preparing',
    isSupported,
    interimTranscript,
    start,
    stop,
  };
}
